import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Mail, ArrowLeft, Loader2, CheckCircle, HelpCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/lib/supabase';

const forgotPasswordSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
});

type ForgotPasswordForm = z.infer<typeof forgotPasswordSchema>;

export default function ForgotPassword() {
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState<string>('');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordForm>({
    resolver: zodResolver(forgotPasswordSchema),
  });

  const onSubmit = async (data: ForgotPasswordForm) => {
    setIsLoading(true);
    setError(null);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(data.email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) {
        throw error;
      }

      setSubmittedEmail(data.email);
      setIsSuccess(true);
    } catch (err: unknown) {
      console.error('Forgot password error:', err);
      const error = err as { message?: string };
      // Don't reveal if email exists or not for security
      setError(error.message || 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-zinc-100 dark:bg-zinc-950">
      {/* Header Bar */}
      <header className="bg-zinc-950 dark:bg-black text-white px-6 py-3 flex items-center justify-between border-b border-zinc-900">
        <div className="flex items-center gap-2">
          <img src="/blazepos-logo.png" alt="BlazePOS" className="h-6 w-6 object-contain" />
          <span className="font-semibold text-lg tracking-tight">BlazePOS</span>
        </div>
        <a
          href="mailto:support@blazepointofsale.com"
          className="text-sm text-zinc-300 hover:text-white transition-colors flex items-center gap-1"
        >
          <HelpCircle className="h-4 w-4" />
          Help
        </a>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex">
        {/* Left Side - Form */}
        <div className="w-full lg:w-1/2 flex items-center justify-center p-8 lg:p-16 bg-white dark:bg-zinc-900">
          <div className="w-full max-w-md">
            {isSuccess ? (
              <div className="text-center space-y-4">
                <div className="w-16 h-16 mx-auto rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                  <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
                </div>
                <h2 className="text-2xl font-semibold text-zinc-900 dark:text-white">Check your email</h2>
                <p className="text-zinc-500 dark:text-zinc-400">
                  We've sent a password reset link to{' '}
                  <span className="font-medium text-zinc-700 dark:text-zinc-300">{submittedEmail}</span>
                </p>
                <p className="text-sm text-zinc-400 dark:text-zinc-500">
                  Didn't receive the email? Check your spam folder or{' '}
                  <button
                    onClick={() => {
                      setIsSuccess(false);
                      setSubmittedEmail('');
                    }}
                    className="text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    try again
                  </button>
                </p>
                <div className="pt-4">
                  <Link to="/login">
                    <Button variant="outline" className="w-full h-11">
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Back to login
                    </Button>
                  </Link>
                </div>
              </div>
            ) : (
              <>
                <div className="mb-8">
                  <h1 className="text-2xl font-semibold text-zinc-900 dark:text-white mb-2">
                    Forgot your password?
                  </h1>
                  <p className="text-sm text-zinc-500 dark:text-zinc-400">
                    Enter your email address and we'll send you a link to reset your password.
                  </p>
                </div>

                {error && (
                  <div className="mb-6 p-3 rounded-lg bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 text-sm">
                    {error}
                  </div>
                )}

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
                      Email address
                    </Label>
                    <div className="relative">
                      <Input
                        id="email"
                        type="email"
                        placeholder="you@example.com"
                        className="h-11 px-4 pr-10 bg-white dark:bg-zinc-800 border-zinc-300 dark:border-zinc-600 text-zinc-900 dark:text-white placeholder:text-zinc-400 dark:placeholder:text-zinc-500 focus:border-blue-500 focus:ring-blue-500"
                        {...register('email')}
                      />
                      <Mail className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400 dark:text-zinc-500" />
                    </div>
                    {errors.email && (
                      <p className="text-sm text-red-600 dark:text-red-400">{errors.email.message}</p>
                    )}
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      'Send reset link'
                    )}
                  </Button>

                  <div className="pt-2">
                    <Link
                      to="/login"
                      className="flex items-center justify-center gap-2 text-sm text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-300"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      Back to login
                    </Link>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>

        {/* Right Side - Hero Image */}
        <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-blue-600 via-blue-500 to-blue-400 items-center justify-center p-12 relative overflow-hidden">
          {/* Decorative elements */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-20 left-10 w-64 h-64 bg-white rounded-full blur-3xl" />
            <div className="absolute bottom-20 right-10 w-96 h-96 bg-white rounded-full blur-3xl" />
          </div>
          
          {/* Content */}
          <div className="relative z-10 text-center">
            {/* POS Terminal Illustration */}
            <div className="relative mx-auto w-80 h-80 mb-8">
              {/* Monitor */}
              <div className="absolute inset-x-4 top-0 h-56 bg-zinc-900 rounded-2xl shadow-2xl border-4 border-zinc-800 overflow-hidden">
                {/* Screen */}
                <div className="m-3 h-44 bg-zinc-800 rounded-lg overflow-hidden">
                  {/* Mock POS Interface */}
                  <div className="p-2 h-full flex">
                    {/* Sidebar */}
                    <div className="w-8 bg-zinc-700/50 rounded mr-2 flex flex-col gap-1 p-1">
                      <div className="w-full h-4 bg-orange-500/80 rounded" />
                      <div className="w-full h-4 bg-zinc-600 rounded" />
                      <div className="w-full h-4 bg-zinc-600 rounded" />
                      <div className="w-full h-4 bg-zinc-600 rounded" />
                    </div>
                    {/* Main content */}
                    <div className="flex-1 grid grid-cols-3 gap-1">
                      <div className="bg-purple-500/60 rounded" />
                      <div className="bg-amber-500/60 rounded" />
                      <div className="bg-green-500/60 rounded" />
                      <div className="bg-blue-500/60 rounded" />
                      <div className="bg-rose-500/60 rounded" />
                      <div className="bg-cyan-500/60 rounded" />
                      <div className="bg-indigo-500/60 rounded" />
                      <div className="bg-orange-500/60 rounded" />
                      <div className="bg-teal-500/60 rounded" />
                    </div>
                  </div>
                </div>
              </div>
              {/* Stand */}
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-20 h-16 bg-zinc-800 rounded-b-xl" />
              {/* Base */}
              <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-32 h-4 bg-zinc-900 rounded-full shadow-lg" />
            </div>

            <h2 className="text-2xl font-semibold text-white mb-3">
              Modern Point of Sale
            </h2>
            <p className="text-blue-100 text-lg max-w-sm mx-auto">
              Streamline your business with our powerful, intuitive POS system
            </p>

            {/* Feature badges */}
            <div className="flex flex-wrap justify-center gap-3 mt-6">
              <span className="px-3 py-1.5 bg-white/20 rounded-full text-white text-sm backdrop-blur-sm">
                Real-time Analytics
              </span>
              <span className="px-3 py-1.5 bg-white/20 rounded-full text-white text-sm backdrop-blur-sm">
                Multi-location
              </span>
              <span className="px-3 py-1.5 bg-white/20 rounded-full text-white text-sm backdrop-blur-sm">
                Cloud-based
              </span>
              <span className="px-3 py-1.5 bg-white/20 rounded-full text-white text-sm backdrop-blur-sm">
                Desktop & Mobile Apps
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-zinc-50 dark:bg-zinc-800 border-t border-zinc-200 dark:border-zinc-700 px-6 py-4">
        <div className="flex items-center justify-between text-sm text-zinc-500 dark:text-zinc-400">
          <p>© {new Date().getFullYear()} BlazePOS. All rights reserved.</p>
          <div className="flex items-center gap-4">
            <a href="https://www.blazepointofsale.com/privacy" className="hover:text-zinc-700 dark:hover:text-zinc-300">
              Privacy Policy
            </a>
            <a href="https://www.blazepointofsale.com/terms" className="hover:text-zinc-700 dark:hover:text-zinc-300">
              Terms of Service
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
